/**
 * EF-CREW-INTAKE-WORKSPACE-V1: Crew Intake Workspace
 * 
 * High-throughput crew onboarding workspace.
 * Target: 80-120 packets processed per hour.
 * 
 * Layout:
 * - LEFT: Packet Queue (pending, processing, completed, rejected)
 * - CENTER: Document Viewer (zoom, rotate, original/processed)
 * - RIGHT: Extracted Fields with confidence indicators
 * - BOTTOM: Actions (Create Crew, Update Existing, Reject, Next)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Check,
  X,
  ChevronRight,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  AlertTriangle,
  Search,
  Filter,
  Clock,
  Users,
  Inbox,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Copy,
  ExternalLink,
  Merge,
  Split,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import api, { findIntakeMatches } from '../lib/api';
import { cn } from '../lib/utils';

// EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1: Import document review component
import { CandidateDocumentReview } from '../components/crew/CandidateDocumentReview';

// EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Import conflict resolution components
import { MatchResultsCard, ConflictResolutionDialog } from '../components/intake/ConflictResolutionDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status colors and labels
const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    icon: Clock,
  },
  processing: {
    label: 'Processing',
    color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    icon: Loader2,
  },
  completed: {
    label: 'Completed',
    color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    icon: CheckCircle2,
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-500/10 text-red-400 border-red-500/20',
    icon: XCircle,
  },
};

// Source colors
const SOURCE_CONFIG = {
  email: { label: 'Email', color: 'bg-violet-500/10 text-violet-400' },
  pwa_upload: { label: 'PWA', color: 'bg-cyan-500/10 text-cyan-400' },
  admin_upload: { label: 'Admin', color: 'bg-zinc-500/10 text-zinc-400' },
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  }) + ' ' + date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Confidence indicator component
const ConfidenceIndicator = ({ confidence }) => {
  const level = confidence >= 0.8 ? 'high' : confidence >= 0.5 ? 'medium' : 'low';
  const colors = {
    high: 'bg-emerald-500',
    medium: 'bg-amber-500',
    low: 'bg-red-500',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-1">
            <div className={cn('w-2 h-2 rounded-full', colors[level])} />
            <span className="text-[10px] text-muted-foreground">
              {Math.round(confidence * 100)}%
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>OCR Confidence: {Math.round(confidence * 100)}%</p>
          <p className="text-xs text-muted-foreground">
            {level === 'high' ? 'High confidence' : level === 'medium' ? 'Review recommended' : 'Manual verification needed'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Packet Card component for queue
const PacketCard = ({ packet, isSelected, onClick }) => {
  const StatusIcon = STATUS_CONFIG[packet.status]?.icon || Clock;
  
  return (
    <div
      data-testid={`packet-card-${packet.id}`}
      onClick={onClick}
      className={cn(
        'p-3 rounded-lg border cursor-pointer transition-all',
        'hover:border-primary/50 hover:bg-surface-elevated/50',
        isSelected 
          ? 'border-primary bg-primary/5' 
          : 'border-border bg-surface'
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">
            {packet.candidate_name || packet.sender_email || 'Unknown'}
          </h4>
          <p className="text-xs text-muted-foreground truncate">
            {packet.sender_email || '-'}
          </p>
        </div>
        <Badge className={cn('text-[10px] shrink-0', SOURCE_CONFIG[packet.source]?.color)}>
          {SOURCE_CONFIG[packet.source]?.label}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('text-[10px]', STATUS_CONFIG[packet.status]?.color)}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {STATUS_CONFIG[packet.status]?.label}
          </Badge>
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {packet.document_count || 0}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {formatDate(packet.created_at)}
        </span>
      </div>
    </div>
  );
};

// Document viewer component
const DocumentViewer = ({ document, zoom, rotation, showProcessed }) => {
  if (!document) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <FileText className="w-12 h-12 opacity-30" />
        <p className="ml-3">Select a document to view</p>
      </div>
    );
  }
  
  const fileUrl = `${API_URL}/api/inbox/attachments/${document.id}/file`;
  // EF-FIX-DOCUMENT-PREVIEW-MIME-TYPE-MAPPING-V1: Use includes() for flexible matching
  const mime = (document.mime_type || "").toLowerCase();
  const isImage = mime.includes('image');
  const isPdf = mime.includes('pdf');
  
  return (
    <div className="h-full flex items-center justify-center overflow-auto p-4 bg-zinc-950/50">
      {isImage ? (
        <img
          src={fileUrl}
          alt={document.original_filename}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease',
          }}
        />
      ) : isPdf ? (
        <iframe
          src={fileUrl}
          className="w-full h-full border-0"
          title={document.original_filename}
        />
      ) : (
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm font-medium">{document.original_filename}</p>
          <p className="text-xs text-muted-foreground mt-1">{document.mime_type || 'Unknown type'}</p>
          <Button variant="outline" size="sm" className="mt-4" asChild>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in new tab
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

// Extracted field component
const ExtractedField = ({ 
  label, 
  value, 
  confidence, 
  conflict, 
  onChange, 
  editable = true 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  
  const handleSave = () => {
    onChange?.(editValue);
    setIsEditing(false);
  };
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        {confidence !== undefined && (
          <ConfidenceIndicator confidence={confidence} />
        )}
      </div>
      
      {isEditing ? (
        <div className="flex gap-2">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
          <Button size="sm" onClick={handleSave}>
            <Check className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <div 
          className={cn(
            'p-2 rounded border bg-surface text-sm cursor-pointer',
            'hover:border-primary/50 transition-colors',
            conflict && 'border-amber-500/50 bg-amber-500/5'
          )}
          onClick={() => editable && setIsEditing(true)}
        >
          {value || <span className="text-muted-foreground italic">Not detected</span>}
        </div>
      )}
      
      {conflict && (
        <div className="mt-1 p-2 rounded bg-amber-500/10 border border-amber-500/20">
          <p className="text-[10px] text-amber-500 font-medium mb-1">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            Conflicting values detected
          </p>
          {conflict.map((c, i) => (
            <div 
              key={i}
              className="text-xs flex items-center gap-2 cursor-pointer hover:text-primary"
              onClick={() => onChange?.(c.value)}
            >
              <span>{c.value}</span>
              <span className="text-muted-foreground">({Math.round(c.confidence * 100)}%)</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
export default function CrewIntakePage() {
  // State
  const [packets, setPackets] = useState([]);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [queueStats, setQueueStats] = useState({});
  
  // Document viewer state
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showProcessed, setShowProcessed] = useState(false);
  
  // Extracted fields state
  const [extractedFields, setExtractedFields] = useState({});
  const [fieldConflicts, setFieldConflicts] = useState({});
  
  // Dialogs
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  
  // EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1: Document review dialog
  const [showDocReviewDialog, setShowDocReviewDialog] = useState(false);
  const [docReviewCandidateId, setDocReviewCandidateId] = useState(null);
  const [docReviewCandidateName, setDocReviewCandidateName] = useState('');
  
  // Crew search for "Update Existing"
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [staffSearchResults, setStaffSearchResults] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffSearch, setShowStaffSearch] = useState(false);
  
  // EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Match detection and conflict resolution
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [selectedMatchStaff, setSelectedMatchStaff] = useState(null);
  
  // Fetch packets
  const fetchPackets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const res = await api.get(`/intake-packets?${params}`);
      setPackets(res.data.packets || []);
      setQueueStats({
        total: res.data.total,
        pending: res.data.pending_count,
        processing: res.data.processing_count,
        completed: res.data.completed_count,
        rejected: res.data.rejected_count,
      });
    } catch (error) {
      console.error('Failed to fetch packets:', error);
      toast.error('Failed to load intake packets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter]);
  
  // Load packet details
  const loadPacketDetails = useCallback(async (packetId) => {
    try {
      setProcessing(true);
      
      // Start processing the packet
      await api.post(`/intake-packets/${packetId}/start-processing`);
      
      // Run OCR and get merged fields
      const res = await api.post(`/intake-packets/${packetId}/run-ocr`);
      const packet = res.data;
      
      setSelectedPacket(packet);
      setExtractedFields(packet.merged_fields || {});
      setFieldConflicts(packet.field_conflicts || {});
      
      // Select first document
      if (packet.documents?.length > 0) {
        setSelectedDocument(packet.documents[0]);
      }
      
      // Reset viewer state
      setZoom(1);
      setRotation(0);
      
    } catch (error) {
      console.error('Failed to load packet:', error);
      toast.error('Failed to load packet details');
    } finally {
      setProcessing(false);
    }
  }, []);
  
  // Handle packet selection
  const handleSelectPacket = (packet) => {
    if (packet.id === selectedPacket?.id) return;
    loadPacketDetails(packet.id);
  };
  
  // Handle field update
  const handleFieldUpdate = (fieldName, value) => {
    setExtractedFields(prev => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], value },
    }));
  };
  
  // Create new crew
  const handleCreateCrew = async () => {
    if (!selectedPacket) return;
    
    try {
      setProcessing(true);
      
      // Prepare staff data from extracted fields
      const staffData = {
        first_name: extractedFields.first_name?.value || '',
        last_name: extractedFields.last_name?.value || '',
        email: extractedFields.email?.value || '',
        phone: extractedFields.phone?.value || '',
        date_of_birth: extractedFields.date_of_birth?.value || null,
        social_security_number: extractedFields.vitale_number?.value || '',
        address: extractedFields.address?.value || '',
        status: 'active',
      };
      
      // Document mappings (TODO: allow user to specify types)
      const documentMappings = (selectedPacket.documents || []).map(doc => ({
        asset_id: doc.id,
        document_type_id: doc.detected_document_type || 'other',
      }));
      
      await api.post(`/intake-packets/${selectedPacket.id}/process`, {
        action: 'create_new',
        staff_data: staffData,
        document_mappings: documentMappings,
      });
      
      toast.success('Crew member created successfully');
      
      // Load next packet
      await loadNextPacket();
      
    } catch (error) {
      console.error('Failed to create crew:', error);
      toast.error('Failed to create crew member');
    } finally {
      setProcessing(false);
    }
  };
  
  // Update existing crew
  const handleUpdateExisting = async () => {
    if (!selectedPacket || !selectedStaff) {
      toast.error('Please select a crew member to update');
      return;
    }
    
    try {
      setProcessing(true);
      
      const staffData = {
        first_name: extractedFields.first_name?.value,
        last_name: extractedFields.last_name?.value,
        email: extractedFields.email?.value,
        phone: extractedFields.phone?.value,
        social_security_number: extractedFields.vitale_number?.value,
        address: extractedFields.address?.value,
      };
      
      const documentMappings = (selectedPacket.documents || []).map(doc => ({
        asset_id: doc.id,
        document_type_id: doc.detected_document_type || 'other',
      }));
      
      await api.post(`/intake-packets/${selectedPacket.id}/process`, {
        action: 'update_existing',
        staff_id: selectedStaff.id,
        staff_data: staffData,
        document_mappings: documentMappings,
      });
      
      toast.success(`Updated ${selectedStaff.first_name} ${selectedStaff.last_name}`);
      setSelectedStaff(null);
      setShowStaffSearch(false);
      
      await loadNextPacket();
      
    } catch (error) {
      console.error('Failed to update crew:', error);
      toast.error('Failed to update crew member');
    } finally {
      setProcessing(false);
    }
  };
  
  // Reject packet
  const handleReject = async () => {
    if (!selectedPacket || !rejectReason.trim()) return;
    
    try {
      setProcessing(true);
      
      await api.post(`/intake-packets/${selectedPacket.id}/reject`, {
        reason: rejectReason,
      });
      
      toast.success('Packet rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      
      await loadNextPacket();
      
    } catch (error) {
      console.error('Failed to reject packet:', error);
      toast.error('Failed to reject packet');
    } finally {
      setProcessing(false);
    }
  };
  
  // Load next packet
  const loadNextPacket = async () => {
    try {
      const res = await api.get(`/intake-packets/next?current_packet_id=${selectedPacket?.id || ''}`);
      
      if (res.data) {
        loadPacketDetails(res.data.id);
      } else {
        setSelectedPacket(null);
        setSelectedDocument(null);
        setExtractedFields({});
        toast.info('No more packets in queue');
      }
      
      // Refresh queue
      fetchPackets();
      
    } catch (error) {
      console.error('Failed to load next packet:', error);
    }
  };
  
  // Search staff for "Update Existing"
  const searchStaff = async (query) => {
    if (!query || query.length < 2) {
      setStaffSearchResults([]);
      return;
    }
    
    try {
      const res = await api.get(`/staff?search=${encodeURIComponent(query)}&limit=10`);
      setStaffSearchResults(res.data || []);
    } catch (error) {
      console.error('Failed to search staff:', error);
    }
  };
  
  // EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Find matching crew members
  const handleFindMatches = async () => {
    if (!selectedPacket) return;
    
    try {
      setMatchLoading(true);
      const res = await findIntakeMatches(selectedPacket.id);
      const data = res.data || res;
      setMatchResults(data);
      
      // If no matches, show toast
      if (data.match_type === 'no_match') {
        toast.success('No duplicates found - safe to create new crew member');
      } else {
        toast.warning(`Found ${data.exact_matches.length} exact and ${data.possible_matches.length} possible matches`);
      }
    } catch (error) {
      console.error('Failed to find matches:', error);
      toast.error('Failed to check for duplicates');
    } finally {
      setMatchLoading(false);
    }
  };
  
  // EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Handle selecting a match for conflict resolution
  const handleSelectMatch = (staff) => {
    setSelectedMatchStaff(staff);
    setShowConflictDialog(true);
  };
  
  // EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Handle resolution complete
  const handleResolutionComplete = () => {
    setMatchResults(null);
    setSelectedMatchStaff(null);
    toast.success('Crew profile updated successfully');
    loadNextPacket();
  };
  
  // Effects
  useEffect(() => {
    fetchPackets();
  }, [fetchPackets]);
  
  useEffect(() => {
    const timer = setTimeout(() => searchStaff(staffSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [staffSearchQuery]);
  
  return (
    <div data-testid="crew-intake-workspace" className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-surface flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Crew Intake Workspace</h1>
          <p className="text-sm text-muted-foreground">
            Process onboarding documents • {queueStats.pending || 0} pending
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchPackets}
            disabled={loading}
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Packet Queue */}
        <div className="w-80 border-r bg-surface-elevated flex flex-col">
          {/* Queue Filters */}
          <div className="p-3 border-b space-y-2">
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="pwa_upload">PWA</SelectItem>
                  <SelectItem value="admin_upload">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Queue Stats */}
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-amber-500">{queueStats.pending || 0} pending</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-blue-400">{queueStats.processing || 0} processing</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-emerald-500">{queueStats.completed || 0} done</span>
            </div>
          </div>
          
          {/* Packet List */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : packets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No packets found</p>
                </div>
              ) : (
                packets.map(packet => (
                  <PacketCard
                    key={packet.id}
                    packet={packet}
                    isSelected={selectedPacket?.id === packet.id}
                    onClick={() => handleSelectPacket(packet)}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>
        
        {/* CENTER: Document Viewer */}
        <div className="flex-1 flex flex-col bg-zinc-950">
          {/* Document Tabs */}
          {selectedPacket?.documents?.length > 0 && (
            <div className="p-2 border-b bg-surface-elevated flex items-center gap-2">
              <ScrollArea className="flex-1" orientation="horizontal">
                <div className="flex gap-1">
                  {selectedPacket.documents.map((doc, idx) => (
                    <Button
                      key={doc.id}
                      variant={selectedDocument?.id === doc.id ? 'secondary' : 'ghost'}
                      size="sm"
                      className="text-xs shrink-0"
                      onClick={() => setSelectedDocument(doc)}
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      {doc.original_filename?.substring(0, 20) || `Doc ${idx + 1}`}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
              
              {/* Viewer Controls */}
              <div className="flex items-center gap-1 border-l pl-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setZoom(z => Math.max(0.25, z - 0.25))}
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setRotation(r => (r + 90) % 360)}
                >
                  <RotateCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Document Display */}
          <div className="flex-1 overflow-auto">
            {selectedPacket ? (
              <DocumentViewer
                document={selectedDocument}
                zoom={zoom}
                rotation={rotation}
                showProcessed={showProcessed}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Select a packet to begin processing</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* RIGHT: Extracted Fields */}
        <div className="w-96 border-l bg-surface flex flex-col">
          {selectedPacket ? (
            <>
              {/* Packet Info Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">Extracted Data</h3>
                  <Badge className={cn('text-[10px]', STATUS_CONFIG[selectedPacket.status]?.color)}>
                    {STATUS_CONFIG[selectedPacket.status]?.label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedPacket.document_count} document(s) • {selectedPacket.source}
                </p>
              </div>
              
              {/* Fields */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  <ExtractedField
                    label="First Name"
                    value={extractedFields.first_name?.value}
                    confidence={extractedFields.first_name?.confidence}
                    conflict={fieldConflicts.first_name}
                    onChange={(v) => handleFieldUpdate('first_name', v)}
                  />
                  <ExtractedField
                    label="Last Name"
                    value={extractedFields.last_name?.value}
                    confidence={extractedFields.last_name?.confidence}
                    conflict={fieldConflicts.last_name}
                    onChange={(v) => handleFieldUpdate('last_name', v)}
                  />
                  <ExtractedField
                    label="Date of Birth"
                    value={extractedFields.date_of_birth?.value}
                    confidence={extractedFields.date_of_birth?.confidence}
                    conflict={fieldConflicts.date_of_birth}
                    onChange={(v) => handleFieldUpdate('date_of_birth', v)}
                  />
                  <ExtractedField
                    label="Email"
                    value={extractedFields.email?.value}
                    confidence={extractedFields.email?.confidence}
                    conflict={fieldConflicts.email}
                    onChange={(v) => handleFieldUpdate('email', v)}
                  />
                  <ExtractedField
                    label="Phone"
                    value={extractedFields.phone?.value}
                    confidence={extractedFields.phone?.confidence}
                    conflict={fieldConflicts.phone}
                    onChange={(v) => handleFieldUpdate('phone', v)}
                  />
                  <ExtractedField
                    label="Vitale Number (SSN)"
                    value={extractedFields.vitale_number?.value}
                    confidence={extractedFields.vitale_number?.confidence}
                    conflict={fieldConflicts.vitale_number}
                    onChange={(v) => handleFieldUpdate('vitale_number', v)}
                  />
                  <ExtractedField
                    label="Address"
                    value={extractedFields.address?.value}
                    confidence={extractedFields.address?.confidence}
                    conflict={fieldConflicts.address}
                    onChange={(v) => handleFieldUpdate('address', v)}
                  />
                  
                  {/* Experience / Notes */}
                  {extractedFields.experience && (
                    <ExtractedField
                      label="Experience / Notes"
                      value={extractedFields.experience?.value}
                      confidence={extractedFields.experience?.confidence}
                      onChange={(v) => handleFieldUpdate('experience', v)}
                    />
                  )}
                </div>
              </ScrollArea>
              
              {/* Actions */}
              <div className="p-4 border-t space-y-3">
                {/* EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Match Detection */}
                <div className="space-y-2">
                  <Button
                    data-testid="check-duplicates-btn"
                    variant="outline"
                    className="w-full"
                    onClick={handleFindMatches}
                    disabled={matchLoading || selectedPacket.status === 'completed'}
                  >
                    {matchLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Check for Duplicates
                  </Button>
                  
                  {/* Match Results */}
                  {matchResults && (
                    <MatchResultsCard
                      matches={matchResults}
                      onSelectMatch={handleSelectMatch}
                      loading={matchLoading}
                    />
                  )}
                </div>
                
                <Separator className="my-3" />
                
                {/* Crew Search for Update Existing */}
                {showStaffSearch && (
                  <div className="p-3 border rounded-lg bg-surface-elevated space-y-2">
                    <Label className="text-xs">Find existing crew member</Label>
                    <Input
                      placeholder="Search by name or email..."
                      value={staffSearchQuery}
                      onChange={(e) => setStaffSearchQuery(e.target.value)}
                      className="h-8"
                    />
                    {staffSearchResults.length > 0 && (
                      <div className="max-h-32 overflow-auto space-y-1">
                        {staffSearchResults.map(staff => (
                          <div
                            key={staff.id}
                            className={cn(
                              'p-2 rounded cursor-pointer text-sm',
                              'hover:bg-primary/10',
                              selectedStaff?.id === staff.id && 'bg-primary/20'
                            )}
                            onClick={() => setSelectedStaff(staff)}
                          >
                            {staff.first_name} {staff.last_name}
                            <span className="text-xs text-muted-foreground ml-2">
                              {staff.email}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedStaff && (
                      <Button 
                        className="w-full" 
                        onClick={handleUpdateExisting}
                        disabled={processing}
                      >
                        {processing ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Update {selectedStaff.first_name}
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid="create-crew-btn"
                    onClick={handleCreateCrew}
                    disabled={processing || selectedPacket.status === 'completed'}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <User className="w-4 h-4 mr-2" />
                    )}
                    Create Crew
                  </Button>
                  <Button
                    data-testid="update-existing-btn"
                    variant="outline"
                    onClick={() => setShowStaffSearch(!showStaffSearch)}
                    disabled={processing || selectedPacket.status === 'completed'}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Update Existing
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid="reject-packet-btn"
                    variant="destructive"
                    onClick={() => setShowRejectDialog(true)}
                    disabled={processing || selectedPacket.status === 'completed'}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    data-testid="next-packet-btn"
                    variant="secondary"
                    onClick={loadNextPacket}
                    disabled={processing}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                
                {/* EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1: Document Review Button */}
                {selectedPacket.candidate_id && (
                  <Button
                    data-testid="review-documents-btn"
                    variant="outline"
                    className="w-full mt-2 bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20"
                    onClick={() => {
                      setDocReviewCandidateId(selectedPacket.candidate_id);
                      setDocReviewCandidateName(selectedPacket.candidate_name || 'Candidate');
                      setShowDocReviewDialog(true);
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Review Documents
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              <div className="text-center p-4">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a packet to view extracted fields</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Packet</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this intake packet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!rejectReason.trim() || processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Reject Packet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* EF-CREW-ONBOARDING-STATUS-AND-DOCUMENT-APPROVAL-V1: Document Review Dialog */}
      <Dialog open={showDocReviewDialog} onOpenChange={setShowDocReviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document Review</DialogTitle>
            <DialogDescription>
              Review and approve/reject documents for {docReviewCandidateName}
            </DialogDescription>
          </DialogHeader>
          {docReviewCandidateId && (
            <CandidateDocumentReview 
              candidateId={docReviewCandidateId}
              candidateName={docReviewCandidateName}
              onStatusChange={() => fetchPackets()}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        packetId={selectedPacket?.id}
        selectedStaff={selectedMatchStaff}
        incomingFields={selectedPacket?.merged_fields}
        onResolved={handleResolutionComplete}
      />
    </div>
  );
}
